import { useState, useRef, useEffect } from 'preact/hooks'
import { Upload } from 'lucide-preact'
import type { LandingPageContextDto, LandingPageViewDto, PageMetaDto } from '@/types/landingPage'
import { BRAND_CLASSES } from '@/constants/theme'
import { landingPagesApi } from '@/api/landingPages'
import { assetApi } from '@/api/asset'

interface PageMetaSectionProps {
  /** KB-resolved context used as the fallback preview */
  context: LandingPageContextDto | null
  /** Per-page override values (may be empty until the user sets something) */
  pageMeta: PageMetaDto | null
  /** Called with the next override value whenever the user edits a text field */
  onPageMetaChange?: (next: PageMetaDto) => void
  /** Called after a successful asset upload with the full updated landing page */
  onLandingPageUpdate?: (updated: LandingPageViewDto) => void
  /** Workspace ID for upload API calls */
  workspaceId?: string
  /** Web ID for upload API calls */
  webId?: string
  /** Disable all inputs (read-only or mid-save) */
  disabled?: boolean
}

/**
 * "Page Meta" sidebar section. Shows the KB-resolved OG image / favicon /
 * meta values as read-only previews labeled "from KB" and renders file upload
 * controls for per-page image overrides plus text inputs for alt text and
 * meta description. All fields are optional — leave them empty to inherit
 * from KB.
 */
export function PageMetaSection({ context, pageMeta, onPageMetaChange, onLandingPageUpdate, workspaceId, webId, disabled }: PageMetaSectionProps) {
  const effectiveOgImage = pageMeta?.OgImageOverrideUrl || context?.OgImageUrl
  const effectiveFavicon = pageMeta?.FaviconOverrideUrl || context?.FaviconUrl

  const ogInputRef = useRef<HTMLInputElement>(null)
  const faviconInputRef = useRef<HTMLInputElement>(null)

  const [ogUploading, setOgUploading] = useState(false)
  const [faviconUploading, setFaviconUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  // Field length caps from the server's FieldLimits catalog so the text-input
  // maxLength matches the API validator exactly.
  const [maxLengths, setMaxLengths] = useState<Record<string, number | undefined>>({})

  useEffect(() => {
    let cancelled = false
    assetApi.getFieldLimits('LandingPage').then(res => {
      if (cancelled) return
      if (res.data) {
        const next: Record<string, number | undefined> = {}
        for (const f of res.data.Fields) next[f.FieldKey] = f.MaxLength
        setMaxLengths(next)
      }
    })
    return () => { cancelled = true }
  }, [])

  const ogImageAltMax = maxLengths['OgImageAlt']
  const metaDescriptionMax = maxLengths['MetaDescription']
  const ogImageAltValue = pageMeta?.OgImageAlt || ''
  const metaDescriptionValue = pageMeta?.MetaDescription || ''
  const ogImageAltOver = ogImageAltMax != null && ogImageAltValue.length > ogImageAltMax
  const metaDescriptionOver = metaDescriptionMax != null && metaDescriptionValue.length > metaDescriptionMax

  // Uploads work both pre- and post-generation. Pre-generation the server just
  // stages the blob and returns the URL — we keep it in local PageMeta so it
  // rides into the Generate request body. Post-generation the server also
  // updates the LP and returns the new view.
  const canUpload = !!workspaceId && !!webId

  const handleField = <K extends keyof PageMetaDto>(key: K, value: string) => {
    if (!onPageMetaChange) return
    const next: PageMetaDto = { ...(pageMeta || {}), [key]: value || null }
    onPageMetaChange(next)
  }

  const handleUpload = async (assetType: 'OgImage' | 'Favicon', file: File) => {
    if (!workspaceId || !webId) return

    const setUploading = assetType === 'OgImage' ? setOgUploading : setFaviconUploading
    const urlField: keyof PageMetaDto = assetType === 'OgImage' ? 'OgImageOverrideUrl' : 'FaviconOverrideUrl'
    setUploading(true)
    setUploadError(null)

    try {
      const res = await landingPagesApi.uploadMetaAsset(workspaceId, webId, assetType, file)
      if (res.data) {
        // Always update local PageMeta with the returned URL so the preview
        // appears immediately and the URL is included in the Generate request
        // body if the LP doesn't exist yet.
        if (onPageMetaChange) {
          onPageMetaChange({ ...(pageMeta || {}), [urlField]: res.data.AssetUrl })
        }
        // If the LP already existed, the server also updated it — propagate
        // the full view so the parent's landingPage state stays fresh.
        if (res.data.LandingPage && onLandingPageUpdate) {
          onLandingPageUpdate(res.data.LandingPage)
        }
      } else {
        setUploadError(res.error || `Failed to upload ${assetType === 'OgImage' ? 'OG image' : 'favicon'}`)
      }
    } catch {
      setUploadError(`Failed to upload ${assetType === 'OgImage' ? 'OG image' : 'favicon'}`)
    } finally {
      setUploading(false)
    }
  }

  const handleFileChange = (assetType: 'OgImage' | 'Favicon') => (e: Event) => {
    const target = e.target as HTMLInputElement
    const file = target.files?.[0]
    if (!file) return

    // Validate image type
    if (!file.type.startsWith('image/')) {
      setUploadError('Please select an image file')
      target.value = ''
      return
    }

    // Validate size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Image must be under 5MB')
      target.value = ''
      return
    }

    handleUpload(assetType, file)
    target.value = ''
  }

  const inputClass = `w-full text-sm px-3 py-2 rounded-md border ${BRAND_CLASSES.INPUT.NORMAL_BORDER} ${BRAND_CLASSES.INPUT.NORMAL_BG} text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50`
  const uploadBtnClass = `flex items-center justify-center gap-2 w-full px-3 py-2 text-sm border-2 border-dashed rounded-lg transition-colors border-slate-300 dark:border-slate-600 hover:border-primary-400 dark:hover:border-primary-500 disabled:opacity-50 disabled:pointer-events-none`

  return (
    <div className="space-y-4">
      {uploadError && (
        <p className="text-sm text-red-600 dark:text-red-400">{uploadError}</p>
      )}

      {/* OG Image */}
      <div>
        <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
          OG Image
        </label>
        {effectiveOgImage && (
          <div className="mb-2 flex items-center gap-2">
            <img src={effectiveOgImage} alt="OG preview" className="h-16 w-auto max-w-[200px] rounded border border-slate-200 dark:border-slate-600 object-contain bg-white" />
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {pageMeta?.OgImageOverrideUrl ? 'Page override' : 'from KB'}
            </span>
          </div>
        )}
        <input
          ref={ogInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange('OgImage')}
          className="hidden"
        />
        <button
          type="button"
          disabled={disabled || ogUploading || !canUpload}
          onClick={() => ogInputRef.current?.click()}
          className={uploadBtnClass}
        >
          <Upload className="w-4 h-4 text-slate-500 dark:text-slate-400" />
          <span className="text-slate-600 dark:text-slate-300">
            {ogUploading ? 'Uploading...' : 'Upload OG image'}
          </span>
        </button>
        <input
          type="text"
          value={ogImageAltValue}
          disabled={disabled}
          placeholder="OG image alt text (optional)"
          onInput={(e) => handleField('OgImageAlt', (e.target as HTMLInputElement).value)}
          maxLength={ogImageAltMax}
          className={`${inputClass} mt-2${ogImageAltOver ? ' border-red-500 dark:border-red-500' : ''}`}
        />
        {ogImageAltMax != null && (
          <div className={`text-xs mt-1 text-right ${ogImageAltOver ? 'text-red-500 dark:text-red-400' : 'text-slate-400 dark:text-slate-500'}`}>
            {ogImageAltValue.length} / {ogImageAltMax}
          </div>
        )}
      </div>

      {/* Favicon */}
      <div>
        <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
          Favicon
        </label>
        {effectiveFavicon && (
          <div className="mb-2 flex items-center gap-2">
            <img src={effectiveFavicon} alt="Favicon preview" className="h-8 w-8 rounded border border-slate-200 dark:border-slate-600 object-contain bg-white" />
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {pageMeta?.FaviconOverrideUrl ? 'Page override' : 'from KB'}
            </span>
          </div>
        )}
        <input
          ref={faviconInputRef}
          type="file"
          accept="image/*,.ico"
          onChange={handleFileChange('Favicon')}
          className="hidden"
        />
        <button
          type="button"
          disabled={disabled || faviconUploading || !canUpload}
          onClick={() => faviconInputRef.current?.click()}
          className={uploadBtnClass}
        >
          <Upload className="w-4 h-4 text-slate-500 dark:text-slate-400" />
          <span className="text-slate-600 dark:text-slate-300">
            {faviconUploading ? 'Uploading...' : 'Upload favicon'}
          </span>
        </button>
      </div>

      {/* Meta description */}
      <div>
        <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
          Meta Description
        </label>
        <textarea
          value={metaDescriptionValue}
          disabled={disabled}
          placeholder="Short description for search engines and social previews"
          rows={3}
          onInput={(e) => handleField('MetaDescription', (e.target as HTMLTextAreaElement).value)}
          maxLength={metaDescriptionMax}
          className={`${inputClass} resize-y${metaDescriptionOver ? ' border-red-500 dark:border-red-500' : ''}`}
        />
        {metaDescriptionMax != null && (
          <div className={`text-xs mt-1 text-right ${metaDescriptionOver ? 'text-red-500 dark:text-red-400' : 'text-slate-400 dark:text-slate-500'}`}>
            {metaDescriptionValue.length} / {metaDescriptionMax}
          </div>
        )}
      </div>
    </div>
  )
}
